import { loadStripe, Stripe } from '@stripe/stripe-js';
import { apiClient } from './api-client';

// Carregar Stripe apenas uma vez
let stripePromise: Promise<Stripe | null>;

export const getStripe = () => {
    if (!stripePromise) {
        const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
        if (!publishableKey) {
            console.error('VITE_STRIPE_PUBLISHABLE_KEY não configurada');
            return Promise.resolve(null);
        }
        stripePromise = loadStripe(publishableKey);
    }
    return stripePromise;
};

export interface CreatePaymentIntentRequest {
    orderId: string;
    amount: number;
    currency?: string;
    metadata?: Record<string, string>;
}

export interface CreateCheckoutSessionRequest {
    cartItems: Array<{
        photoId: string;
        quantity: number;
        unitPrice: number;
        data?: any; // Dados do evento e foto vindos do carrinho
    }>;
    userId: string;
    successUrl: string;
    cancelUrl: string;
}

export interface PaymentIntentResponse {
    success: boolean;
    data: {
        clientSecret: string;
        paymentIntentId: string;
    };
    message: string;
}

export interface CheckoutSessionResponse {
    success: boolean;
    data: {
        sessionId: string;
        url: string;
        orderId: string;
    };
    message: string;
}

export class StripeService {
    /**
     * Cria um PaymentIntent para pagamento único
     */
    static async createPaymentIntent(request: CreatePaymentIntentRequest): Promise<PaymentIntentResponse> {
        try {
            const response = await apiClient.post('/stripe/payment-intent', request);
            return response.data;
        } catch (error) {
            console.error('Erro ao criar PaymentIntent:', error);
            throw new Error('Erro ao criar PaymentIntent');
        }
    }

    /**
     * Cria uma sessão de checkout para múltiplos itens
     */
    static async createCheckoutSession(request: CreateCheckoutSessionRequest): Promise<CheckoutSessionResponse> {
        try {
            // Log para debug - verificar dados sendo enviados
            console.log('🔍 [StripeService] Enviando dados para checkout:', JSON.stringify(request, null, 2));

            const response = await apiClient.post('/stripe/checkout-session', request);
            return response.data;
        } catch (error) {
            console.error('Erro ao criar sessão de checkout:', error);
            throw new Error('Erro ao criar sessão de checkout');
        }
    }

    /**
     * Processa pagamento com PaymentIntent
     */
    static async processPayment(paymentIntentRequest: CreatePaymentIntentRequest) {
        try {
            // Criar PaymentIntent no backend
            const paymentIntentResponse = await this.createPaymentIntent(paymentIntentRequest);

            if (!paymentIntentResponse.success) {
                throw new Error('Falha ao criar PaymentIntent');
            }

            // Carregar Stripe
            const stripe = await getStripe();
            if (!stripe) {
                throw new Error('Stripe não carregado');
            }

            // Confirmar pagamento
            const { error } = await stripe.confirmPayment({
                clientSecret: paymentIntentResponse.data.clientSecret,
                confirmParams: {
                    return_url: `${window.location.origin}/payment/success`,
                },
            });

            if (error) {
                throw new Error(error.message);
            }

            return {
                success: true,
                paymentIntentId: paymentIntentResponse.data.paymentIntentId,
            };
        } catch (error) {
            console.error('Erro ao processar pagamento:', error);
            throw error;
        }
    }

    /**
     * Processa checkout com sessão
     */
    static async processCheckout(checkoutRequest: CreateCheckoutSessionRequest) {
        try {
            // Criar sessão de checkout no backend
            const checkoutResponse = await this.createCheckoutSession(checkoutRequest);

            if (!checkoutResponse.success) {
                throw new Error('Falha ao criar sessão de checkout');
            }

            // Redirecionar para o Stripe Checkout
            window.location.href = checkoutResponse.data.url;

            return {
                success: true,
                sessionId: checkoutResponse.data.sessionId,
                orderId: checkoutResponse.data.orderId,
            };
        } catch (error) {
            console.error('Erro ao processar checkout:', error);
            throw error;
        }
    }

    /**
     * Recupera uma sessão de checkout
     */
    static async getCheckoutSession(sessionId: string) {
        try {
            const response = await apiClient.get(`/stripe/checkout-session/${sessionId}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao recuperar sessão:', error);
            throw new Error('Erro ao recuperar sessão');
        }
    }

    /**
     * Recupera um PaymentIntent
     */
    static async getPaymentIntent(paymentIntentId: string) {
        try {
            const response = await apiClient.get(`/stripe/payment-intent/${paymentIntentId}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao recuperar PaymentIntent:', error);
            throw new Error('Erro ao recuperar PaymentIntent');
        }
    }

    /**
     * Cria um reembolso
     */
    static async createRefund(paymentIntentId: string, amount?: number) {
        try {
            const response = await apiClient.post('/stripe/refund', {
                paymentIntentId,
                amount,
            });
            return response.data;
        } catch (error) {
            console.error('Erro ao criar reembolso:', error);
            throw new Error('Erro ao criar reembolso');
        }
    }

    /**
     * Confirma pagamento via checkout session (fallback quando webhook não é chamado)
     */
    static async confirmCheckoutSession(sessionId: string) {
        try {
            const response = await apiClient.post(`/stripe/checkout-session/${sessionId}/confirm`);
            return response.data;
        } catch (error) {
            console.error('Erro ao confirmar checkout session:', error);
            throw new Error('Erro ao confirmar checkout session');
        }
    }
}
